import React from 'react';
import { FiSend, FiPackage, FiMapPin, FiUser, FiMail, FiPhone, FiMessageSquare, FiTruck } from 'react-icons/fi';
import { FormField } from '../../ui/FormField';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';
import { Textarea } from '../../ui/Textarea';
import { Button } from '../../ui/Button';
import { Spinner } from '../../ui/Spinner';
import { getCargoTypes, getShipmentTypes } from '../../../utils/constants';
import { useLanguage } from '../../../contexts/LanguageContext';

/**
 * Quote form component
 *
 * @param {Object} props
 * @param {Object} props.form - Form values
 * @param {Function} props.onChange - Change handler
 * @param {Function} props.onSubmit - Submit handler
 * @param {Function} props.onCancel - Cancel handler
 * @param {boolean} props.isSubmitting - Submitting state
 */
export const QuoteForm = ({ form, onChange, onSubmit, onCancel, isSubmitting }) => {
  const { t } = useLanguage();
  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-6 p-6">
      {/* Cargo Info Section */}
      <div className="flex flex-col gap-4">
        <h3 className="text-xl font-bold text-gray-900 border-b-2 border-primary pb-2">
          {t('quote.cargoInfo')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField label={t('quote.cargoTypeLabel')} icon={FiPackage} htmlFor="cargoType">
            <Select
              id="cargoType"
              name="cargoType"
              value={form.cargoType}
              onChange={onChange}
              options={getCargoTypes(t)}
            />
          </FormField>

          <FormField label={t('quote.shipmentTypeLabel')} icon={FiTruck} htmlFor="shipmentType">
            <Select
              id="shipmentType"
              name="shipmentType"
              value={form.shipmentType}
              onChange={onChange}
              options={getShipmentTypes(t)}
            />
          </FormField>

          <FormField label={t('quote.fromLabel')} icon={FiMapPin} htmlFor="from">
            <Input
              type="text"
              id="from"
              name="from"
              value={form.from}
              onChange={onChange}
              placeholder={t('quote.fromPlaceholder')}
              autoComplete="address-level2"
            />
          </FormField>

          <FormField label={t('quote.toLabel')} icon={FiMapPin} htmlFor="to">
            <Input
              type="text"
              id="to"
              name="to"
              value={form.to}
              onChange={onChange}
              placeholder={t('quote.toPlaceholder')}
              autoComplete="address-level2"
            />
          </FormField>

          <FormField label={t('quote.weightLabel')} htmlFor="weightKg">
            <Input
              type="number"
              id="weightKg"
              name="weightKg"
              value={form.weightKg}
              onChange={onChange}
              placeholder={t('quote.weightPlaceholder')}
              min="0"
            />
          </FormField>

          <FormField label={t('quote.volumeLabel')} htmlFor="volumeM3">
            <Input
              type="number"
              id="volumeM3"
              name="volumeM3"
              value={form.volumeM3}
              onChange={onChange}
              placeholder={t('quote.volumePlaceholder')}
              min="0"
              step="0.01"
            />
          </FormField>
        </div>
      </div>

      {/* Contact Info Section */}
      <div className="flex flex-col gap-4">
        <h3 className="text-xl font-bold text-gray-900 border-b-2 border-primary pb-2">
          {t('quote.contactInfo')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <FormField label={t('quote.companyLabel')} htmlFor="company">
              <Input
                type="text"
                id="company"
                name="company"
                value={form.company}
                onChange={onChange}
                placeholder={t('quote.companyPlaceholder')}
                autoComplete="organization"
              />
            </FormField>
          </div>

          <FormField label={t('quote.firstNameLabel')} icon={FiUser} htmlFor="firstName">
            <Input
              type="text"
              id="firstName"
              name="firstName"
              value={form.firstName}
              onChange={onChange}
              placeholder={t('quote.firstNamePlaceholder')}
              autoComplete="given-name"
            />
          </FormField>

          <FormField label={t('quote.lastNameLabel')} icon={FiUser} htmlFor="lastName">
            <Input
              type="text"
              id="lastName"
              name="lastName"
              value={form.lastName}
              onChange={onChange}
              placeholder={t('quote.lastNamePlaceholder')}
              autoComplete="family-name"
            />
          </FormField>

          <FormField label={t('quote.emailLabel')} icon={FiMail} htmlFor="email">
            <Input
              type="email"
              id="email"
              name="email"
              value={form.email}
              onChange={onChange}
              placeholder={t('quote.emailPlaceholder')}
              autoComplete="email"
            />
          </FormField>

          <FormField label={t('quote.phoneLabel')} icon={FiPhone} htmlFor="phone">
            <Input
              type="tel"
              id="phone"
              name="phone"
              value={form.phone}
              onChange={onChange}
              placeholder={t('quote.phonePlaceholder')}
              autoComplete="tel"
            />
          </FormField>

          <div className="md:col-span-2">
            <FormField label={t('quote.messageLabel')} icon={FiMessageSquare} htmlFor="message">
              <Textarea
                id="message"
                name="message"
                rows="4"
                value={form.message}
                onChange={onChange}
                placeholder={t('quote.messagePlaceholder')}
              />
            </FormField>
          </div>
        </div>
      </div>

      {/* Footer Buttons */}
      <div className="flex gap-4 justify-end pt-4 border-t border-gray-200">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          {t('quote.cancel')}
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Spinner size="sm" />
              {t('quote.submitting')}
            </>
          ) : (
            <>
              <FiSend aria-hidden="true" />
              {t('quote.submit')}
            </>
          )}
        </Button>
      </div>
    </form>
  );
};
